import classNames from "classnames";
import { RefObject } from "react";
import { useTimezoneSelect } from "react-timezone-select";
import { CreatedEvent } from "../../redux/features/event/event.types";
import { SinglePayment } from "../../redux/features/payment/payment.types";
import { tzOptions } from "../../utils/constants";
import { formatDate, formatToCurrency } from "../../utils/helpers";
import { TicketPurchase } from "../../utils/types";
import { Pill } from "../pill";
import { BgWrapper } from "../wrapper/bgWrapper";

type TicketProps = {
  eventName: string;
  startDate: string;
  endDate: string;
  startTime: string;
  location: string;
  qrCode: string;
  firstName: string;
  lastName: string;
  email: string;
  ticketsPurchase: TicketPurchase[];
  ref?: RefObject<HTMLDivElement | null>;
  downloadView?: boolean;
  className?: string;
  event?: CreatedEvent;
  payment?: SinglePayment;
};

export const Ticket = ({
  eventName,
  startDate,
  endDate,
  startTime,
  location,
  qrCode,
  firstName,
  lastName,
  email,
  ticketsPurchase,
  ref,
  downloadView = false,
  className = "",
  event,
  payment,
}: TicketProps) => {
  const encoded = encodeURIComponent(qrCode.trim());
  const dataUrl = `data:image/svg+xml;charset=utf-8,${encoded}`;

  const { parseTimezone } = useTimezoneSelect(tzOptions);

  return (
    <div
      className={classNames(
        "h-fit w-full",
        {
          "max-w-[460px]": !downloadView,
          "flex max-w-[595px] flex-col items-center justify-center !bg-grey-50 px-[60px] py-4":
            downloadView,
        },
        className,
      )}
      ref={ref}
    >
      {downloadView && (
        <img
          src="/assets/logo_full.svg"
          className="mx-auto mb-8 w-full max-w-[96px] text-center"
          alt="logo"
        />
      )}

      <BgWrapper className="flex w-full max-w-[460px] flex-col items-center justify-center gap-[48px] !p-4">
        <div className="w-full border-b pb-6 text-sm text-grey-600">
          <p className="text-32 mb-2 font-fraunces text-primary">{eventName}</p>
          <p className="font-bold">
            {formatDate(startDate, "ddd, MMMM DD, YYYY", true)}{" "}
            {startDate === endDate
              ? ""
              : `- ${formatDate(endDate, "ddd, MMMM DD, YYYY", true)}`}
            , {startTime}{" "}
            {event?.timezone
              ? `(${parseTimezone(event.timezone)?.abbrev})`
              : ""}
          </p>
          <p>{location}</p>
        </div>

        <img
          src={dataUrl}
          alt="ticket-qrCode"
          className="block size-[250px] object-cover"
        />

        <div className="flex w-full items-start gap-5 border-t border-grey-100 pt-6">
          <div className="flex-1 text-sm text-grey-600">
            <p className="text-base font-bold text-primary">
              {firstName} {lastName}
            </p>

            <p>{email}</p>

            {ticketsPurchase.map((tp, idx) => {
              return (
                <p key={`${idx}-${tp.ticket._id}`}>
                  {tp.quantity}x {tp.ticket.name}
                </p>
              );
            })}

            <p className="font-bold">
              {formatToCurrency(payment?.amount ?? 0, {
                currency: payment?.currency,
              })}
            </p>
          </div>

          <Pill text="GUEST" variation="grey" />
        </div>
      </BgWrapper>

      {downloadView && (
        <div className="mt-16 block text-center text-sm text-grey-600">
          <p>Modern Event Platform for Africa</p>
          <p className="mt-[3px]">www.usetikket.com</p>
        </div>
      )}
    </div>
  );
};

export const TicketBody = ({ ...props }: TicketProps) => {
  return (
    <>
      <Ticket {...props} ref={undefined} />
      <Ticket {...props} className="fixed bottom-[100%]" downloadView />
    </>
  );
};
